import { Css } from "@homebound/beam";
import { RuntimeStyle } from "@homebound/truss/runtime";
import { NamedFragment } from "src/fragments/commonFragments.generated";
import { LocationType } from "src/generated/graphqlTypes";
import {
  FinishScheduleVisualModePdfQuery,
  FinishScheduleVisualMode_TliFragment,
  useFinishScheduleVisualModePdfQuery,
} from "src/routes/pdfs/FinishSchedulePdf/FinishScheduleVisualModePdf.generated";
import { fail, queryResult } from "src/utils";
import { PRODUCT_FALLBACK_IMG_URL } from "src/utils/constants";
import { ArrayParam, StringParam, useQueryParams } from "use-query-params";

export function FinishScheduleVisualModePdf() {
  const [{ designPackageId, designRpavId, planRpavIds, combinations, costCodeIds, locationInPath }] = useQueryParams({
    designPackageId: StringParam,
    // TODO: update backend url?
    designRpavId: StringParam,
    planRpavIds: ArrayParam,
    combinations: ArrayParam,
    costCodeIds: ArrayParam,
    locationInPath: ArrayParam,
  });

  const parsedCombinations = parseCombinationsFilterArray(combinations);

  const query = useFinishScheduleVisualModePdfQuery({
    variables: {
      versionId: designRpavId ?? fail("Design Package Rpav Id is required"),
      designPackageId: designPackageId ?? fail("Design Package Id is required"),
      filter: {
        designRpavId: designRpavId ?? fail("Design Package Rpav Id is required"),
        combinations: parsedCombinations,
        planRpavIds: parseFilterArray(planRpavIds),
        costCodeIds: parseFilterArray(costCodeIds),
        locationInPath: parseFilterArray(locationInPath),
      },
    },
  });

  return queryResult(query, (data) => <VisualModePdfView data={data} combinations={parsedCombinations ?? []} />);
}

function VisualModePdfView(props: { data: FinishScheduleVisualModePdfQuery; combinations: string[][] }) {
  const { data, combinations } = props;
  // Generate all possible combinations of forDesignPackage GOGs
  const idps = getIndividualDesignPackages(data, combinations);
  return (
    <>
      <RuntimeStyle css={{ "@page": { size: "landscape" } }} />
      {idps.map(({ id, ...rest }, index) => (
        <IndividualDesignPackageView
          key={id} // Ensure each page has a unique key
          isFirst={index === 0}
          version={data.readyPlanAggregateVersion.version}
          id={id}
          {...rest}
        />
      ))}
    </>
  );
}

function IndividualDesignPackageView(
  props: IndividualDesignPackage & { version: number | null | undefined; isFirst: boolean },
) {
  const { rpos, locations, order, version, isFirst } = props;
  // Spec Level group should always be ordered first ==> [Deluxe, Contemporary, Light]
  const [primaryRpoName, ...otherRpoNames] = rpos.map((rpo) => rpo.name);

  // After troubleshooting, we found that the background color wasn't filling the entire page.
  // The standard size of a PDF page is 8.5x11 inches, which translates to 816px x 1056px.
  // To ensure the background color covers the entire page, we set the margins to 0 and adjusted the height and width to 1056px and 816px, respectively.

  const pageHeight = 816;
  const pageWidth = 1056;

  return (
    <div css={Css.wPx(pageWidth).$} key={order} style={isFirst ? {} : { pageBreakBefore: "always" }}>
      <div /* Cover Page */ css={Css.hPx(pageHeight).df.fdc.p4.add("backgroundColor", "rgba(235, 234, 228, 1)").$}>
        <div css={Css.df.jcsb.$}>
          <img src="/wordmark.svg" alt="Homebound" css={Css.hPx(40).$} />
          {version && isFirst && <span>v{version}</span>}
        </div>
        <div css={Css.hPx(600).df.fdc.jcc.gap2.$}>
          <div css={Css.xl2.fontFamily("PT Serif").$}>{otherRpoNames.join(" ") ?? ""}</div>
          <div css={Css.df.gap1.$}>
            <img css={Css.hPx(40).$} src="/images/spec-level-pdf-icon.svg" alt="" />
            <div css={Css.p1.fw(400).color("rgba(198, 151, 117, 1)").fs("26px").$}>
              {/* Spec Level Name */}
              {primaryRpoName}
            </div>
          </div>
        </div>
      </div>
      {/* Loop over each location to create a page, i.e. Kitchen, Whole House, etc. */}
      {locations.map((locationGroup) => {
        return (
          <div key={locationGroup.location?.id}>
            {/* Loop through each group of TLIs within a location, setting groups of 10 to limit the number displayed per page. */}
            {locationGroup.locationTlis.batched(10).map((tlis, i) => (
              <div key={i} style={{ pageBreakBefore: "always" }} css={Css.hPx(pageHeight).df.fdc.p4.$}>
                <div /* Location Header */ css={Css.df.jcsb.$}>
                  <div css={Css.xl2.fontFamily("PT Serif").$}>{locationGroup.location?.name}</div>
                  <div css={Css.df.gap1.$}>
                    <img css={Css.hPx(26).$} src="/images/spec-level-pdf-icon.svg" alt="" />
                    <div css={Css.pPx(2).fw(400).color("rgba(198, 151, 117, 1)").fs("20px").$}>
                      {/* Spec Level Name */}
                      {primaryRpoName}
                    </div>
                  </div>
                </div>

                {/* Setting a height here will contain the product cards and prevent them from spilling onto the next page if a card's height exceeds a certain limit. */}
                <div /* List of Product (tli) Cards */ css={Css.df.fww.gap3.pt2.hPx(700).$}>
                  {tlis.map((tli) => (
                    <div key={tli.id} css={Css.wPx(175).$}>
                      <div /* Product Image */ css={Css.hPx(175).w100.ba.bcGray200.bw2.p1.df.aic.jcc.$}>
                        <img
                          src={tli.materialVariant?.featuredImage?.asset?.previewUrl ?? PRODUCT_FALLBACK_IMG_URL}
                          alt={tli.item.name}
                          css={Css.maxw100.maxh100.$}
                        />
                      </div>
                      <div /* Product Details */ css={Css.df.fdc.mt1.$}>
                        <div css={Css.fontFamily("PT Serif").$}>
                          <a
                            css={Css.smSb.$}
                            href={tli.materialVariant?.blueprintUrl.url}
                            target="_blank"
                            rel="noreferrer"
                          >
                            {tli.item.name}
                          </a>
                          {/* We're already grouped by room, if we're a tli within that room specify where in the room */}
                          {(tli.location.type.code === LocationType.Feature ||
                            tli.location.type.code === LocationType.Scope) && (
                            <span css={Css.fs("12px").$}> ({tli.location.name})</span>
                          )}
                        </div>
                        <div css={Css.sm.fs("12px").$}>{tli.materialVariant?.listing.brand?.name}</div>
                        <div css={Css.fs("12px").$}>{tli.materialVariant?.listing.name}</div>
                        {tli.materialVariant?.materialAttributeValues
                          .filter((value) => value.dimension.useInVisualModePdf)
                          .map((value) => (
                            <div key={value.id} css={Css.fs("12px").$}>
                              {value.dimension.name}: {value.textValue}
                              {value.dimension.unitOfMeasure?.abbreviation.toLowerCase()}
                            </div>
                          ))}
                        {tli.materialVariant?.manufacturerUrl && (
                          <a
                            css={Css.fs("12px").$}
                            href={tli.materialVariant?.manufacturerUrl}
                            target="_blank"
                            rel="noreferrer"
                          >
                            Manufacturer URL
                          </a>
                        )}
                      </div>
                    </div>
                  ))}
                </div>

                <div /* Footer */ css={Css.df.jcfe.$}>
                  <div>
                    <img src="/wordmark.svg" alt="Homebound" css={Css.hPx(30).$} />
                  </div>
                </div>
              </div>
            ))}
          </div>
        );
      })}
    </div>
  );
}

type IndividualDesignPackage = {
  id: string;
  name: string;
  rpos: NamedFragment[];
  order: number;
  locations: { location: NamedFragment | undefined; locationTlis: FinishScheduleVisualMode_TliFragment[] }[];
};

function getIndividualDesignPackages(
  data: FinishScheduleVisualModePdfQuery,
  combinations: string[][],
): IndividualDesignPackage[] {
  const { designPackage, locations } = data;
  const tlis = data.designPackageFinishSchedule.entities;
  const combinationIds = combinations.map((c) => c.join(","));
  const filteredCombinations = designPackage.combinations.filter((c) => combinationIds.includes(c.id));

  // Each combination represents an IDP
  return filteredCombinations
    .map(({ id, name, options }) => ({
      id,
      name,
      rpos: options,
      order: options.sum((rpo) => rpo.order),
      locations: tlis
        // Filter TLIs looking for options that matches this IDP/RPO combination
        .filter((tli) =>
          tli.targetCombinations.some(({ options: targetCombOptions }) =>
            targetCombOptions.every((rpo) => options.map(({ id }) => id).includes(rpo.id)),
          ),
        )
        .groupByObject((tli) => tli.location?.room?.id ?? tli.location?.id ?? "none")
        .map(([locationId, locationTlis]) => ({ location: locations.find((l) => l.id === locationId), locationTlis })),
    }))
    .sortByKey("order");
}

export function parseFilterArray(filter: (string | null)[] | null | undefined) {
  // If the filter is not empty, split the string by comma and map the values to an array of strings, i.e. ["1,2,3"] => ["1", "2", "3"]
  return filter?.nonEmpty ? filter.first!.split(",").map((id) => id) : undefined;
}

export function parseCombinationsFilterArray(filter: (string | null)[] | null | undefined) {
  // If the filter is not empty, split the string by comma and map the values to an array of strings, i.e. [["1,2,3"]] => [["1", "2", "3"]]
  return filter?.nonEmpty ? filter.map((f) => f?.split(",")).compact() : undefined;
}
